import { createFileRoute, Link } from "@tanstack/react-router";
export const Route = createFileRoute("/admin/_layout/crm/reports")({
  component: CrmReportsPage,
});

import { useState } from "react";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DateSelector } from "@/components/crud/filters/DateSelector";
import { useCrmReports, type FetchCrmReportsParams } from "@/hooks/api/crm/useReports";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import { StatCard } from "@/components/reusable-components/stat-card";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  CartesianGrid,
} from "recharts";
import { Skeleton } from "@/components/ui/skeleton";
import { Users, Activity, TrendingUp, Building2 } from "lucide-react";
import { dayjs } from "@/lib/date-utils";
import type { DateRange } from "react-day-picker";

const PIE_COLORS = ["#3b82f6", "#8b5cf6", "#f59e0b", "#10b981", "#ef4444", "#6b7280", "#ec4899"];

const cardClassName = "bg-linear-to-t from-primary/10 to-card shadow-xs";

function CrmReportsPage() {
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [showFilters, setShowFilters] = useState(false);
  const [reportParams, setReportParams] = useState<FetchCrmReportsParams>({});

  const reportsQuery = useCrmReports(reportParams);
  const leadsBySource = reportsQuery.data?.leadsBySource ?? [];
  const leadsByStatus = reportsQuery.data?.leadsByStatus ?? [];
  const conversionRate = reportsQuery.data?.conversionRate;
  const pipeline = reportsQuery.data?.pipeline ?? [];
  const accountsByType = reportsQuery.data?.accountsByType ?? [];
  const isLoading = reportsQuery.isPending;
  const isFetching = reportsQuery.isFetching;

  const handleApplyDateRange = (range?: DateRange) => {
    setDateRange(range);
    setReportParams({
      date_from: range?.from ? dayjs(range.from).format("YYYY-MM-DD") : undefined,
      date_to: range?.to ? dayjs(range.to).format("YYYY-MM-DD") : undefined,
    });
  };

  const handleClearFilters = () => {
    setDateRange(undefined);
    setReportParams({});
  };

  const sourceData = leadsBySource.map((i) => ({ name: i.label, count: i.count }));
  const statusData = leadsByStatus.map((i) => ({ name: i.label, count: i.count }));
  const pipelineData = pipeline.map((i) => ({ name: i.label, amount: parseFloat(i.total_amount || "0") }));
  const accountTypeData = accountsByType.map((i) => ({ name: i.label, count: i.count }));

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="CRM Reports"
          description="Analytics and insights for your CRM data."
          onToggleFilter={() => setShowFilters((p) => !p)}
          isLoading={isFetching}
        />

        <FiltersPanel show={showFilters} onClear={handleClearFilters} isLoading={isFetching}>
          <DateSelector
            value={dateRange}
            onApply={handleApplyDateRange}
            onClear={handleClearFilters}
            label="Filter by date range"
          />
        </FiltersPanel>

        {/* Conversion Rate */}
        {conversionRate && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Link to="/admin/crm/leads" className="block no-underline">
              <StatCard label="Total Leads" value={conversionRate.total_leads} loading={isLoading} className={cardClassName} />
            </Link>
            <Link to="/admin/crm/leads" className="block no-underline">
              <StatCard label="Converted Leads" value={conversionRate.converted_leads} loading={isLoading} className={cardClassName} />
            </Link>
            <StatCard label="Conversion Rate" value={`${conversionRate.conversion_rate.toFixed(1)}%`} loading={isLoading} className={cardClassName} />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Leads by Source */}
          <Card className={cardClassName}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Users className="h-4 w-4" />
                Leads by Source
              </CardTitle>
              <CardDescription>Distribution of leads across acquisition channels.</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-65 w-full rounded-md" />
              ) : sourceData.length === 0 ? (
                <div className="flex h-65 items-center justify-center text-sm text-muted-foreground">No data available</div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={sourceData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Leads by Status */}
          <Card className={cardClassName}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Activity className="h-4 w-4" />
                Leads by Status
              </CardTitle>
              <CardDescription>Breakdown of leads by their current status.</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-65 w-full rounded-md" />
              ) : statusData.length === 0 ? (
                <div className="flex h-65 items-center justify-center text-sm text-muted-foreground">No data available</div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={statusData} cx="50%" cy="50%" outerRadius={90} dataKey="count" label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}>
                      {statusData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Legend />
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Pipeline by Stage */}
          <Card className={cardClassName}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <TrendingUp className="h-4 w-4" />
                Opportunity Pipeline by Stage
              </CardTitle>
              <CardDescription>Total opportunity value grouped by pipeline stage.</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-65 w-full rounded-md" />
              ) : pipelineData.length === 0 ? (
                <div className="flex h-65 items-center justify-center text-sm text-muted-foreground">No data available</div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={pipelineData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => [`$${Number(v).toLocaleString()}`, "Amount"]} />
                    <Bar dataKey="amount" fill="#10b981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Accounts by Type */}
          <Card className={cardClassName}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Building2 className="h-4 w-4" />
                Accounts by Type
              </CardTitle>
              <CardDescription>Distribution of accounts across different types.</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-65 w-full rounded-md" />
              ) : accountTypeData.length === 0 ? (
                <div className="flex h-65 items-center justify-center text-sm text-muted-foreground">No data available</div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={accountTypeData} cx="50%" cy="50%" outerRadius={90} dataKey="count" label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}>
                      {accountTypeData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Legend />
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </SidebarInset>
  );
}
